import React, { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { deriveThirdPartyId, deriveThirdPartyName } from "layouts/agent/utils";
import { ROLE_LEVEL_LABELS, normalizeRoleLevel } from "utils/roleUtils";

const FORMAL_ROLE_LABELS = {
  1: ROLE_LEVEL_LABELS[1],
  2: ROLE_LEVEL_LABELS[2],
  3: ROLE_LEVEL_LABELS[3],
  4: ROLE_LEVEL_LABELS[4],
  5: ROLE_LEVEL_LABELS[5],
  ADMIN: ROLE_LEVEL_LABELS[1],
  MANAGER: ROLE_LEVEL_LABELS[2],
  USER: ROLE_LEVEL_LABELS[3],
  SITE_OPERATOR: ROLE_LEVEL_LABELS[5],
};

const LEGACY_ROLE_LABELS = {
  admin: ROLE_LEVEL_LABELS[1],
  manager: ROLE_LEVEL_LABELS[2],
  user: ROLE_LEVEL_LABELS[3],
  site_operator: ROLE_LEVEL_LABELS[5],
};

const DOWNSTREAM_LEGACY_ROLE_OPTIONS = {
  1: ["admin", "manager", "user", "site_operator"],
  2: ["manager", "user", "site_operator"],
  3: ["user", "site_operator"],
  4: ["site_operator"],
  5: [],
};

const LEGACY_ROLE_RANKS = {
  admin: 1,
  manager: 2,
  user: 3,
  site_operator: 5,
};

const compareRoleOptions = (left, right) => {
  const leftRank = Number.isInteger(left?.roleRank) ? left.roleRank : Number.MAX_SAFE_INTEGER;
  const rightRank = Number.isInteger(right?.roleRank) ? right.roleRank : Number.MAX_SAFE_INTEGER;
  if (leftRank !== rightRank) return leftRank - rightRank;
  return String(left?.label || "").localeCompare(String(right?.label || ""), "zh-Hant");
};

const mergeRoleOptions = (...groups) => {
  const merged = new Map();
  groups.flat().forEach((option) => {
    if (!option?.value) return;
    const key = String(option.value);
    if (!merged.has(key)) {
      merged.set(key, { ...option, value: key });
      return;
    }

    const existing = merged.get(key);
    merged.set(key, {
      ...existing,
      ...option,
      value: key,
      label: option.label || existing.label,
      name: option.name || existing.name,
      roleRank: Number.isInteger(option.roleRank) ? option.roleRank : existing.roleRank,
    });
  });

  return Array.from(merged.values()).sort(compareRoleOptions);
};

const STATUS_LABELS = {
  active: "啟用",
  inactive: "停用",
  pending: "待處理",
};

const STATUS_CHIP_PROPS = {
  active: {
    color: "success",
    variant: "filled",
  },
  inactive: {
    color: "default",
    variant: "outlined",
  },
  pending: {
    color: "warning",
    variant: "filled",
  },
};

const IAM_ROLES_QUERY = `
  query GetIamRoles($email: String!) {
    GetIamRoles(email: $email)
  }
`;

const UPDATE_IAM_USER_MUTATION = `
  mutation UpdateIamUser($input: IamUserUpdateInput!) {
    UpdateIamUser(input: $input)
  }
`;

const CREATE_IAM_MISSION_MUTATION = `
  mutation CreateIamMission($input: IamCreateInput!) {
    CreateIamMission(input: $input)
  }
`;

const canViewRoleEditor = (roleLevel) => [1, 2, 3, 4].includes(normalizeRoleLevel(roleLevel));

const normalizeRoleName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeRoleToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const parseLegacyRoles = (item) => {
  const tokens = [];
  if (Array.isArray(item?.roles)) tokens.push(...item.roles);
  if (item?.role) tokens.push(...String(item.role).split(","));
  return Array.from(new Set(tokens.map(normalizeRoleName).filter(Boolean)));
};

const resolveRoleRank = (item) => {
  const directCandidates = [item?.role_rank, item?.roleRank, item?.role_level, item?.roleLevel];
  for (const candidate of directCandidates) {
    const numeric = Number(candidate);
    if (Number.isInteger(numeric) && numeric > 0) return numeric;
  }

  const legacyRoles = parseLegacyRoles(item);
  const legacyRanks = legacyRoles
    .map((legacyRole) => LEGACY_ROLE_RANKS[legacyRole])
    .filter((value) => Number.isInteger(value));

  if (legacyRanks.length) return Math.min(...legacyRanks);
  return null;
};

const canManageByRoleRank = (actorRoleRank, targetRoleRank) => {
  if (!Number.isInteger(actorRoleRank) || !Number.isInteger(targetRoleRank)) return null;
  if (actorRoleRank === 4) return actorRoleRank < targetRoleRank;
  return actorRoleRank <= targetRoleRank;
};

const canAssignOptionByRoleRank = (actorRoleRank, optionRoleRank) => {
  if (!Number.isInteger(actorRoleRank) || !Number.isInteger(optionRoleRank)) return null;
  if (actorRoleRank === 4) return actorRoleRank < optionRoleRank;
  return actorRoleRank <= optionRoleRank;
};

const isReadOnlyRoleByRank = (actorRoleRank) =>
  Number.isInteger(actorRoleRank) && actorRoleRank >= 4;

const formatFormalRoleLabel = (item) => {
  if (item?.role_name_v2) return String(item.role_name_v2);
  const directKeys = [
    item?.role_code,
    item?.roleCode,
    item?.role_rank,
    item?.roleRank,
    item?.role_level,
    item?.roleLevel,
    item?.id,
  ];

  for (const key of directKeys) {
    const match = FORMAL_ROLE_LABELS[normalizeRoleToken(key)];
    if (match) return match;
  }

  if (item?.role_name) return String(item.role_name);
  if (item?.roleName) return String(item.roleName);

  const name = normalizeRoleToken(item?.name);
  if (FORMAL_ROLE_LABELS[name]) return FORMAL_ROLE_LABELS[name];
  return item?.name ? String(item.name) : "";
};

const formatFormalRoleDisplay = (item, fallback = "") => {
  if (item?.role_name_v2) return String(item.role_name_v2);
  const mapped = parseLegacyRoles(item)
    .map((token) => FORMAL_ROLE_LABELS[normalizeRoleToken(token)] || "")
    .filter(Boolean);

  if (mapped.length) return mapped.join(", ");
  const directLabel = formatFormalRoleLabel(item);
  if (directLabel) return directLabel;
  return fallback;
};

const formatConservativeRoleDisplay = (item) => {
  const formalDisplay = formatFormalRoleDisplay(item);
  if (formalDisplay && formalDisplay !== "-") return formalDisplay;

  const legacyRoles = parseLegacyRoles(item);
  if (!legacyRoles.length) return "-";

  const labels = legacyRoles
    .map((legacyRole) => LEGACY_ROLE_LABELS[legacyRole] || legacyRole)
    .filter(Boolean);
  return labels.length ? Array.from(new Set(labels)).join(", ") : "-";
};

const resolveRoleValueByIdentifiers = (item, options) => {
  const identifierCandidates = [
    item?.role_id,
    item?.roleId,
    item?.target_role_id,
    item?.targetRoleId,
    item?.id,
  ]
    .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
    .map((value) => String(value));

  for (const candidate of identifierCandidates) {
    const match = options.find((option) => option.value === candidate);
    if (match) return match.value;
  }

  const roleLevelCandidates = [item?.role_level, item?.roleLevel]
    .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
    .map((value) => String(value));

  for (const candidate of roleLevelCandidates) {
    const match = options.find((option) => String(option.roleLevel || "") === candidate);
    if (match) return match.value;
  }

  return "";
};

const resolveRoleValueByLabels = (item, options) => {
  const labelCandidates = [item?.role_name_v2, item?.role_name, item?.roleName, item?.name]
    .filter((value) => String(value || "").trim() !== "")
    .map(normalizeRoleName);

  for (const candidate of labelCandidates) {
    const match = options.find(
      (option) =>
        normalizeRoleName(option.label) === candidate ||
        normalizeRoleName(option.name) === candidate
    );
    if (match) return match.value;
  }

  return "";
};

const resolveRoleValue = (item, options) => {
  if (!item || !Array.isArray(options) || !options.length) return "";

  const matchedByIdentifiers = resolveRoleValueByIdentifiers(item, options);
  if (matchedByIdentifiers) return matchedByIdentifiers;

  const matchedByLabels = resolveRoleValueByLabels(item, options);
  if (matchedByLabels) return matchedByLabels;

  const nameCandidates = [];
  if (Array.isArray(item.roles)) {
    nameCandidates.push(...item.roles);
  }
  if (item.role) {
    nameCandidates.push(...String(item.role).split(","));
  }

  const normalizedCandidates = nameCandidates.map(normalizeRoleName).filter(Boolean);
  for (const candidate of normalizedCandidates) {
    const match = options.find(
      (option) =>
        normalizeRoleName(option.name) === candidate ||
        normalizeRoleName(option.label) === candidate
    );
    if (match) return match.value;
  }

  return "";
};

const buildRoleOption = (item) => {
  if (!item) return null;
  const rawValue =
    item?.id ?? item?.role_id ?? item?.roleId ?? item?.target_role_id ?? item?.targetRoleId ?? "";
  if (rawValue === "" || rawValue === undefined || rawValue === null) return null;

  const roleRank = resolveRoleRank(item);
  return {
    value: String(rawValue),
    label: formatFormalRoleLabel(item) || (item?.name ? String(item.name) : `Role ${rawValue}`),
    roleId: item?.id ?? item?.role_id ?? item?.roleId ?? null,
    roleLevel: item?.role_level ?? item?.roleLevel ?? roleRank ?? null,
    name: item?.name ? String(item.name) : "",
    roleRank,
  };
};

const formatStatusLabel = (value) => {
  const normalized = normalizeStatus(value);
  return STATUS_LABELS[normalized] || value || "-";
};

function UserManagementRoleEditor() {
  const { roleLevel, userData } = useUserContext() || {};
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const email = userData?.email ?? "";
  const canView = canViewRoleEditor(roleLevel);
  const isRecordNotFound = (err) =>
    String(err || "")
      .toLowerCase()
      .includes("record not found");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingEmail, setSavingEmail] = useState("");
  const [rows, setRows] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [thirdPartyOptions, setThirdPartyOptions] = useState([]);
  const [thirdPartyError, setThirdPartyError] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");
  const [formValues, setFormValues] = useState({
    target_email: "",
    target_first_name: "",
    target_last_name: "",
    target_role_id: "",
    target_third_party_id: "",
    iam_start_time: "",
    iam_end_time: "",
    description: "",
  });
  const selectMenuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          maxHeight: 320,
        },
      },
    }),
    []
  );
  const selectFieldSx = useMemo(
    () => ({
      minWidth: 220,
      "& .MuiInputBase-root": {
        minHeight: 44,
      },
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
      },
    }),
    []
  );
  const currentUserRoleRank = useMemo(() => {
    return normalizeRoleLevel(roleLevel);
  }, [roleLevel]);
  const isReadOnlyRoleManager = useMemo(
    () => isReadOnlyRoleByRank(currentUserRoleRank),
    [currentUserRoleRank]
  );
  const allowedLegacyRoles = useMemo(
    () => DOWNSTREAM_LEGACY_ROLE_OPTIONS[currentUserRoleRank] || [],
    [currentUserRoleRank]
  );
  const filteredRoleOptions = useMemo(
    () =>
      roleOptions.filter((option) => {
        if (isReadOnlyRoleManager) return false;
        const rankBasedDecision = canAssignOptionByRoleRank(currentUserRoleRank, option.roleRank);
        if (rankBasedDecision !== null) return rankBasedDecision;
        return allowedLegacyRoles.includes(normalizeRoleName(option.name));
      }),
    [allowedLegacyRoles, currentUserRoleRank, isReadOnlyRoleManager, roleOptions]
  );

  useEffect(() => {
    setFormValues((prev) => {
      const nextRoleId = filteredRoleOptions[0]?.value || "";
      if (!nextRoleId) {
        return prev.target_role_id ? { ...prev, target_role_id: "" } : prev;
      }

      const stillAllowed = filteredRoleOptions.some(
        (option) => option.value === prev.target_role_id
      );
      return stillAllowed
        ? prev
        : {
            ...prev,
            target_role_id: nextRoleId,
          };
    });
  }, [filteredRoleOptions]);

  const flattenThirdParties = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    const stack = [...nodes];
    const result = [];
    while (stack.length) {
      const node = stack.shift();
      if (!node) continue;
      result.push(node);
      const children = node.children || node.Children;
      if (Array.isArray(children) && children.length) {
        stack.push(...children);
      }
    }
    return result;
  };

  const fetchUsers = async () => {
    if (!email) return;

    setLoading(true);
    setError("");
    setThirdPartyError("");
    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: IAM_ROLES_QUERY,
          variables: { email },
        }),
      });

      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      if (json?.errors?.length) {
        const message = json.errors[0]?.message || "GraphQL error";
        if (isRecordNotFound(message)) {
          setRoleOptions([]);
          setRows([]);
        } else {
          throw new Error(message);
        }
      }
      if (!json?.errors?.length) {
        const payload = json?.data?.GetIamRoles;
        const data = payload?.data || {};
        const iamList = Array.isArray(data.iam_list) ? data.iam_list : [];
        const currentRoles = Array.isArray(data.role) ? data.role : [];
        const currentRole = currentRoles[0] || null;
        const currentEmail = String(data?.iam?.email || "")
          .trim()
          .toLowerCase();
        const roleList = Array.isArray(data.role_list) ? data.role_list : [];

        const currentRoleOptions = currentRoles.map(buildRoleOption).filter(Boolean);
        const assignableRoleOptions = roleList.map(buildRoleOption).filter(Boolean);
        const allKnownRoleOptions = mergeRoleOptions(currentRoleOptions, assignableRoleOptions);
        const availableAssignableRoleOptions = allKnownRoleOptions.filter((option) => {
          if (isReadOnlyRoleManager) return false;
          const rankBasedDecision = canAssignOptionByRoleRank(currentUserRoleRank, option.roleRank);
          if (rankBasedDecision !== null) return rankBasedDecision;
          return allowedLegacyRoles.includes(normalizeRoleName(option.name));
        });

        setRoleOptions(allKnownRoleOptions);

        const nextRows = iamList.map((item) => {
          const rowEmail = String(item?.email || "")
            .trim()
            .toLowerCase();
          const isCurrentUser = Boolean(currentEmail) && rowEmail === currentEmail;
          const roleSource = isCurrentUser && currentRole ? currentRole : item;
          const currentRoleValue = isCurrentUser
            ? resolveRoleValue(currentRole, allKnownRoleOptions) ||
              resolveRoleValue(item, allKnownRoleOptions)
            : resolveRoleValue(item, allKnownRoleOptions);
          const currentRoleOption =
            allKnownRoleOptions.find((option) => option.value === currentRoleValue) ||
            buildRoleOption(roleSource) ||
            buildRoleOption(item);
          const rowRoleOptions = mergeRoleOptions(
            availableAssignableRoleOptions,
            currentRoleOption ? [currentRoleOption] : []
          );
          const selectedRoleId =
            currentRoleValue ||
            (isCurrentUser
              ? resolveRoleValue(currentRole, rowRoleOptions) ||
                resolveRoleValue(item, rowRoleOptions)
              : resolveRoleValue(item, rowRoleOptions));
          const legacyRoles = parseLegacyRoles(item);
          const displayRole =
            formatFormalRoleDisplay(roleSource) ||
            formatConservativeRoleDisplay(roleSource) ||
            formatConservativeRoleDisplay(item);
          const rowRoleRank = isCurrentUser
            ? resolveRoleRank(currentRole) || resolveRoleRank(item)
            : resolveRoleRank(item);
          const rankBasedDecision = canManageByRoleRank(currentUserRoleRank, rowRoleRank);
          const rowCanEdit =
            currentUserRoleRank === 1
              ? true
              : isReadOnlyRoleManager
              ? false
              : rankBasedDecision !== null
              ? rankBasedDecision
              : allowedLegacyRoles.length > 0 &&
                legacyRoles.length > 0 &&
                !legacyRoles.includes("admin");

          return {
            email: item?.email || "",
            first_name: item?.first_name || "",
            last_name: item?.last_name || "",
            status: item?.status || "",
            third_party_id: item?.third_party_id ?? "",
            roleDisplay: displayRole,
            legacyRoles,
            rowRoleRank,
            rowCanEdit,
            rowRoleOptions,
            assignableRoleIds: availableAssignableRoleOptions.map((option) => option.value),
            selectedRoleId,
            originalRoleId: selectedRoleId,
            rowError: "",
            rowSuccess: "",
          };
        });

        setRows(nextRows);
      }
    } catch (err) {
      console.error(err);
      setError("載入使用者管理資料失敗");
    }

    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            query GetMdmThirdPartyList($email: String!) {
              GetMdmThirdPartyList(email: $email)
            }
          `,
          variables: { email },
        }),
      });

      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      if (json?.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }

      const payload = json?.data?.GetMdmThirdPartyList;
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const flatNodes = flattenThirdParties(data);
      const options = flatNodes
        .map((node) => {
          const rawValue = deriveThirdPartyId(node);
          const numericValue = Number(rawValue);
          if (!rawValue || Number.isNaN(numericValue)) return null;
          return {
            value: String(numericValue),
            label: deriveThirdPartyName(node),
          };
        })
        .filter(Boolean);

      setThirdPartyOptions(options);
      if (options.length) {
        setFormValues((prev) => ({
          ...prev,
          target_third_party_id: prev.target_third_party_id || options[0].value,
        }));
      }
    } catch (err) {
      console.error(err);
      setThirdPartyError("載入第三方失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [allowedLegacyRoles, backendUrl, currentUserRoleRank, email, isReadOnlyRoleManager]);

  const handleRoleChange = (targetEmail, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.email === targetEmail
          ? {
              ...row,
              selectedRoleId: value,
              rowError: "",
              rowSuccess: "",
            }
          : row
      )
    );
  };

  const handleChange = (key) => (event) => {
    setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const resetCreateMessages = () => {
    setCreateError("");
    setCreateSuccess("");
  };

  const handleOpenCreate = () => {
    if (isReadOnlyRoleManager) return;
    resetCreateMessages();
    setOpen(true);
  };

  const handleCloseCreate = () => {
    setOpen(false);
    resetCreateMessages();
  };

  const toISOIfPossible = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString();
  };

  const handleCreate = async () => {
    resetCreateMessages();

    if (isReadOnlyRoleManager) {
      setCreateError("您目前的角色不可建立使用者");
      return;
    }

    if (!email) {
      setCreateError("找不到登入者 Email");
      return;
    }

    const requiredFields = [
      { key: "target_email", label: "要創建的信箱" },
      { key: "target_role_id", label: "角色" },
      { key: "target_third_party_id", label: "綁定第三方" },
      { key: "target_first_name", label: "名字" },
      { key: "target_last_name", label: "姓氏" },
      { key: "iam_start_time", label: "帳號啟用時間" },
      { key: "iam_end_time", label: "帳號結束時間" },
      { key: "description", label: "描述" },
    ];

    const missing = requiredFields.find(({ key }) => !String(formValues[key] || "").trim());
    if (missing) {
      setCreateError(`請填寫 ${missing.label}`);
      return;
    }

    const payload = {
      requestor_email: email,
      target_email: formValues.target_email,
      target_role_id: Number(formValues.target_role_id),
      target_third_party_id: Number(formValues.target_third_party_id),
      target_first_name: formValues.target_first_name.trim(),
      target_last_name: formValues.target_last_name.trim(),
      iam_start_time: toISOIfPossible(formValues.iam_start_time),
      iam_end_time: toISOIfPossible(formValues.iam_end_time),
      description: formValues.description.trim(),
    };

    if (Number.isNaN(payload.target_role_id) || Number.isNaN(payload.target_third_party_id)) {
      setCreateError("角色或第三方格式不正確");
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: CREATE_IAM_MISSION_MUTATION,
          variables: { input: payload },
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      if (json?.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }
      setCreateSuccess("已送出建立任務");
    } catch (err) {
      console.error(err);
      setCreateError("建立任務失敗");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveRole = async (row) => {
    if (!email) return;
    if (!row?.email) return;
    if (!row?.selectedRoleId) {
      setRows((prev) =>
        prev.map((item) =>
          item.email === row.email ? { ...item, rowError: "請先選擇角色", rowSuccess: "" } : item
        )
      );
      return;
    }
    if (!row?.rowCanEdit) {
      setRows((prev) =>
        prev.map((item) =>
          item.email === row.email ? { ...item, rowError: "目前無此權限", rowSuccess: "" } : item
        )
      );
      return;
    }
    const selectedOption =
      row?.rowRoleOptions?.find((option) => option.value === row.selectedRoleId) ||
      roleOptions.find((option) => option.value === row.selectedRoleId);
    const canAssignSelectedRole = Boolean(
      selectedOption &&
        Array.isArray(row?.assignableRoleIds) &&
        row.assignableRoleIds.includes(row.selectedRoleId)
    );
    if (!canAssignSelectedRole) {
      setRows((prev) =>
        prev.map((item) =>
          item.email === row.email
            ? { ...item, rowError: "不可調整為此角色", rowSuccess: "" }
            : item
        )
      );
      return;
    }

    const nextRoleId = Number(row.selectedRoleId);
    if (Number.isNaN(nextRoleId)) {
      setRows((prev) =>
        prev.map((item) =>
          item.email === row.email ? { ...item, rowError: "角色格式不正確", rowSuccess: "" } : item
        )
      );
      return;
    }

    setSavingEmail(row.email);
    setRows((prev) =>
      prev.map((item) =>
        item.email === row.email ? { ...item, rowError: "", rowSuccess: "" } : item
      )
    );

    try {
      const res = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: UPDATE_IAM_USER_MUTATION,
          variables: {
            input: {
              requestor_email: email,
              target_email: row.email,
              target_role_id: nextRoleId,
            },
          },
        }),
      });

      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      if (json?.errors?.length) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }

      setRows((prev) =>
        prev.map((item) =>
          item.email === row.email
            ? {
                ...item,
                originalRoleId: row.selectedRoleId,
                roleDisplay: selectedOption?.label || item.roleDisplay,
                legacyRoles: selectedOption?.name
                  ? [normalizeRoleName(selectedOption.name)]
                  : item.legacyRoles,
                rowRoleRank: selectedOption?.roleRank ?? item.rowRoleRank,
                rowError: "",
                rowSuccess: "角色已更新",
              }
            : item
        )
      );
    } catch (err) {
      console.error(err);
      setRows((prev) =>
        prev.map((item) =>
          item.email === row.email ? { ...item, rowError: "更新角色失敗", rowSuccess: "" } : item
        )
      );
    } finally {
      setSavingEmail("");
    }
  };

  const columns = useMemo(
    () => [
      { Header: "Email", accessor: "email" },
      { Header: "姓氏", accessor: "last_name" },
      { Header: "名字", accessor: "first_name" },
      { Header: "狀態", accessor: "status" },
      { Header: "綁定第三方", accessor: "third_party_id" },
      { Header: "角色", accessor: "role_editor", disableSortBy: true },
      { Header: "操作", accessor: "actions", disableSortBy: true },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      rows.map((row) => {
        const isDirty = row.selectedRoleId !== row.originalRoleId;
        const isSaving = savingEmail === row.email;
        const canEditRow = Boolean(row.rowCanEdit) && Array.isArray(row.rowRoleOptions);
        const hasEditableOptions =
          canEditRow && Array.isArray(row.assignableRoleIds) && row.assignableRoleIds.length > 0;
        const normalizedStatus = normalizeStatus(row.status);
        const statusChipProps = STATUS_CHIP_PROPS[normalizedStatus] || STATUS_CHIP_PROPS.inactive;

        return {
          email: row.email || "-",
          last_name: row.last_name || "-",
          first_name: row.first_name || "-",
          status: (
            <Chip
              size="small"
              label={formatStatusLabel(row.status)}
              color={statusChipProps.color}
              variant={statusChipProps.variant}
            />
          ),
          third_party_id: row.third_party_id === "" ? "-" : row.third_party_id,
          role_editor: (
            <Stack spacing={1} sx={{ minWidth: 220 }}>
              {hasEditableOptions ? (
                <>
                  <MDInput
                    select
                    value={row.selectedRoleId}
                    onChange={(event) => handleRoleChange(row.email, event.target.value)}
                    SelectProps={{ MenuProps: selectMenuProps }}
                    sx={selectFieldSx}
                  >
                    {row.rowRoleOptions.length ? (
                      row.rowRoleOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        無可用角色
                      </MenuItem>
                    )}
                  </MDInput>
                </>
              ) : null}
              {!hasEditableOptions ? (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {canEditRow ? row.roleDisplay || "無可編輯角色" : "目前無此權限"}
                </MDTypography>
              ) : null}
              {row.rowError ? (
                <MDTypography variant="caption" color="error" fontWeight="medium">
                  {row.rowError}
                </MDTypography>
              ) : null}
              {row.rowSuccess ? (
                <MDTypography variant="caption" color="success" fontWeight="medium">
                  {row.rowSuccess}
                </MDTypography>
              ) : null}
            </Stack>
          ),
          actions: (
            <MDButton
              variant={isDirty ? "gradient" : "outlined"}
              color={isDirty ? "info" : "secondary"}
              size="small"
              disabled={!hasEditableOptions || !isDirty || isSaving}
              onClick={() => handleSaveRole(row)}
            >
              {isSaving ? "儲存中..." : "儲存角色"}
            </MDButton>
          ),
        };
      }),
    [rows, savingEmail, selectFieldSx, selectMenuProps]
  );

  if (!canView) return null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2.5}
                px={2.5}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    使用者管理
                  </MDTypography>
                </MDBox>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <MDButton
                    variant="outlined"
                    color="white"
                    onClick={fetchUsers}
                    disabled={loading}
                  >
                    重新整理
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="warning"
                    onClick={handleOpenCreate}
                    disabled={isReadOnlyRoleManager}
                  >
                    建立使用者
                  </MDButton>
                </Stack>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                {loading && (
                  <MDTypography variant="button" color="text" fontWeight="medium" mb={1}>
                    載入中...
                  </MDTypography>
                )}
                {error && (
                  <MDTypography variant="button" color="error" fontWeight="medium" mb={2}>
                    {error}
                  </MDTypography>
                )}
                <DataTable
                  table={{ columns, rows: tableRows }}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                  canSearch
                  showTotalEntries={false}
                  isSorted
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={open}
        onClose={handleCloseCreate}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            px: 1,
          },
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" px={3} pt={3}>
          <MDTypography variant="h6">建立使用者</MDTypography>
          <IconButton onClick={handleCloseCreate} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
        <DialogContent>
          <Stack spacing={2.5}>
            <MDInput label="請求 Email" fullWidth value={email} disabled />
            <MDInput
              label="要創建的信箱"
              type="email"
              fullWidth
              value={formValues.target_email}
              onChange={handleChange("target_email")}
            />
            <MDInput
              label="名字"
              fullWidth
              value={formValues.target_first_name}
              onChange={handleChange("target_first_name")}
            />
            <MDInput
              label="姓氏"
              fullWidth
              value={formValues.target_last_name}
              onChange={handleChange("target_last_name")}
            />
            <MDInput
              select
              label="角色"
              fullWidth
              value={formValues.target_role_id}
              onChange={handleChange("target_role_id")}
              SelectProps={{ MenuProps: selectMenuProps }}
              sx={selectFieldSx}
            >
              {filteredRoleOptions.length ? (
                filteredRoleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  無可用角色
                </MenuItem>
              )}
            </MDInput>
            <MDInput
              select
              label="綁定第三方"
              fullWidth
              value={formValues.target_third_party_id}
              onChange={handleChange("target_third_party_id")}
              SelectProps={{ MenuProps: selectMenuProps }}
              sx={selectFieldSx}
            >
              {thirdPartyOptions.length ? (
                thirdPartyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  無可用第三方
                </MenuItem>
              )}
            </MDInput>
            {thirdPartyError && (
              <MDTypography variant="button" color="error" fontWeight="medium">
                {thirdPartyError}
              </MDTypography>
            )}
            <MDInput
              label="帳號啟用時間"
              type="datetime-local"
              fullWidth
              value={formValues.iam_start_time}
              onChange={handleChange("iam_start_time")}
              InputLabelProps={{ shrink: true }}
            />
            <MDInput
              label="帳號結束時間"
              type="datetime-local"
              fullWidth
              value={formValues.iam_end_time}
              onChange={handleChange("iam_end_time")}
              InputLabelProps={{ shrink: true }}
            />
            <MDInput
              label="描述"
              multiline
              rows={3}
              fullWidth
              value={formValues.description}
              onChange={handleChange("description")}
            />
            {createError && (
              <MDTypography variant="button" color="error" fontWeight="medium">
                {createError}
              </MDTypography>
            )}
            {createSuccess && (
              <MDTypography variant="button" color="success" fontWeight="medium">
                {createSuccess}
              </MDTypography>
            )}
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              onClick={handleCreate}
              disabled={creating || isReadOnlyRoleManager}
            >
              建立使用者
            </MDButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default UserManagementRoleEditor;
